import { useState, useEffect, useCallback } from 'react'
import { Radar, Search, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

// ── ThoughtWorks-style Technology Radar ──────────────────────────────────────
// Quadrants: Languages & Frameworks, Platforms, Tools, Techniques
// Rings: Adopt, Trial, Assess, Hold

interface RadarItem {
  name: string
  quadrant: 'languages' | 'platforms' | 'tools' | 'techniques'
  ring: 'adopt' | 'trial' | 'assess' | 'hold'
  description: string
  isNew: boolean
}

const RADAR_ITEMS: RadarItem[] = [
  // Languages & Frameworks
  { name: 'TypeScript', quadrant: 'languages', ring: 'adopt', description: 'Default for all new frontend and Node.js projects', isNew: false },
  { name: 'Rust', quadrant: 'languages', ring: 'trial', description: 'Systems programming, CLI tools, WebAssembly. Growing ecosystem.', isNew: false },
  { name: 'Go', quadrant: 'languages', ring: 'adopt', description: 'Backend services, CLIs, infrastructure tooling', isNew: false },
  { name: 'Python', quadrant: 'languages', ring: 'adopt', description: 'AI/ML, data, scripting. uv for package management.', isNew: false },
  { name: 'Zig', quadrant: 'languages', ring: 'assess', description: 'C replacement. Used in Bun, TigerBeetle. Watch this space.', isNew: true },
  { name: 'React', quadrant: 'languages', ring: 'adopt', description: 'Still dominant for web UIs. Server Components maturing.', isNew: false },
  { name: 'Next.js', quadrant: 'languages', ring: 'adopt', description: 'Full-stack React framework. App Router stable.', isNew: false },
  { name: 'HTMX', quadrant: 'languages', ring: 'trial', description: 'Hypermedia-driven approach. Good for server-rendered apps.', isNew: true },
  { name: 'Svelte/SvelteKit', quadrant: 'languages', ring: 'trial', description: 'Compiler-first framework. Great DX.', isNew: false },
  { name: 'Kotlin Multiplatform', quadrant: 'languages', ring: 'assess', description: 'Share code across Android, iOS, web, server.', isNew: true },
  { name: 'Swift (server)', quadrant: 'languages', ring: 'assess', description: 'Vapor framework. Apple pushing server-side Swift.', isNew: true },

  // Platforms
  { name: 'Kubernetes', quadrant: 'platforms', ring: 'adopt', description: 'Container orchestration standard. But consider managed alternatives.', isNew: false },
  { name: 'Cloudflare Workers', quadrant: 'platforms', ring: 'trial', description: 'Edge compute. V8 isolates. R2, D1, KV. Full stack at the edge.', isNew: false },
  { name: 'Vercel', quadrant: 'platforms', ring: 'adopt', description: 'Next.js deployment. Great DX. Serverless functions.', isNew: false },
  { name: 'Fly.io', quadrant: 'platforms', ring: 'trial', description: 'Deploy containers close to users. Good for latency-sensitive apps.', isNew: false },
  { name: 'Railway', quadrant: 'platforms', ring: 'trial', description: 'Simple PaaS. Good Heroku alternative.', isNew: true },
  { name: 'Deno Deploy', quadrant: 'platforms', ring: 'assess', description: 'Edge-first runtime. TypeScript native. Deno 2.0 mature.', isNew: true },
  { name: 'Supabase', quadrant: 'platforms', ring: 'trial', description: 'Open-source Firebase alternative. Postgres + auth + realtime.', isNew: false },
  { name: 'Neon', quadrant: 'platforms', ring: 'trial', description: 'Serverless Postgres with branching. Scale-to-zero.', isNew: true },
  { name: 'AWS Graviton', quadrant: 'platforms', ring: 'adopt', description: 'ARM-based instances. 30-40% better price-performance.', isNew: false },
  { name: 'Oracle OCI', quadrant: 'platforms', ring: 'assess', description: 'Aggressive pricing. Free tier generous. Ampere ARM.', isNew: true },
  { name: 'Hetzner', quadrant: 'platforms', ring: 'trial', description: '5-10x cheaper than hyperscalers for EU workloads. IPv6 native.', isNew: false },

  // Tools
  { name: 'Claude Code', quadrant: 'tools', ring: 'adopt', description: 'Agentic coding assistant. Terminal-native. Best for complex tasks.', isNew: true },
  { name: 'Cursor', quadrant: 'tools', ring: 'trial', description: 'AI-native IDE. Good for inline code generation.', isNew: true },
  { name: 'uv (Python)', quadrant: 'tools', ring: 'adopt', description: 'Extremely fast Python package manager. Replaces pip, venv, poetry.', isNew: true },
  { name: 'Bun', quadrant: 'tools', ring: 'trial', description: 'All-in-one JS runtime. Fast, but ecosystem still catching up.', isNew: false },
  { name: 'Docker', quadrant: 'tools', ring: 'adopt', description: 'Container standard. Desktop getting expensive — consider alternatives.', isNew: false },
  { name: 'Terraform', quadrant: 'tools', ring: 'adopt', description: 'IaC standard. BSL license change — watch OpenTofu.', isNew: false },
  { name: 'OpenTofu', quadrant: 'tools', ring: 'trial', description: 'Open-source Terraform fork. Linux Foundation backed.', isNew: true },
  { name: 'Pulumi', quadrant: 'tools', ring: 'trial', description: 'IaC in real languages (TS, Python, Go). Good DX.', isNew: false },
  { name: 'Grafana', quadrant: 'tools', ring: 'adopt', description: 'Observability dashboards. LGTM stack maturing.', isNew: false },
  { name: 'OpenTelemetry', quadrant: 'tools', ring: 'adopt', description: 'Vendor-neutral observability. Traces, metrics, logs converging.', isNew: false },
  { name: 'Warp terminal', quadrant: 'tools', ring: 'assess', description: 'AI-powered terminal. Rust-based. Interesting but vendor lock-in risk.', isNew: true },
  { name: 'Pkl (Apple)', quadrant: 'tools', ring: 'assess', description: 'Configuration language from Apple. Alternative to YAML/JSON.', isNew: true },

  // Techniques
  { name: 'RAG (Retrieval Augmented Generation)', quadrant: 'techniques', ring: 'adopt', description: 'Essential for enterprise AI. Ground LLMs in your data.', isNew: false },
  { name: 'Agentic AI', quadrant: 'techniques', ring: 'trial', description: 'AI agents with tool use. MCP protocol emerging. Early but powerful.', isNew: true },
  { name: 'Platform Engineering', quadrant: 'techniques', ring: 'adopt', description: 'Internal developer platforms. Golden paths. Self-service infra.', isNew: false },
  { name: 'FinOps', quadrant: 'techniques', ring: 'adopt', description: 'Cloud cost management as engineering discipline.', isNew: false },
  { name: 'Zero Trust', quadrant: 'techniques', ring: 'adopt', description: 'Never trust, always verify. BeyondCorp model.', isNew: false },
  { name: 'GitOps', quadrant: 'techniques', ring: 'adopt', description: 'Git as single source of truth for infra. ArgoCD, Flux.', isNew: false },
  { name: 'Feature Flags', quadrant: 'techniques', ring: 'adopt', description: 'Progressive delivery. LaunchDarkly, Unleash, Flagsmith.', isNew: false },
  { name: 'Shift-Left Security', quadrant: 'techniques', ring: 'adopt', description: 'Security in CI/CD. SAST, DAST, SCA, secrets scanning.', isNew: false },
  { name: 'AI Code Review', quadrant: 'techniques', ring: 'trial', description: 'AI-assisted PR reviews. CodeRabbit, Sourcery, Ellipsis.', isNew: true },
  { name: 'LLMOps', quadrant: 'techniques', ring: 'trial', description: 'Operationalising LLMs. Prompt versioning, eval, monitoring.', isNew: true },
  { name: 'Federated Learning', quadrant: 'techniques', ring: 'assess', description: 'Train models without centralising data. Privacy-preserving.', isNew: false },
  { name: 'Confidential Computing', quadrant: 'techniques', ring: 'assess', description: 'TEE-based workloads. AMD SEV, Intel TDX, ARM CCA.', isNew: false },
  { name: 'Sovereign Cloud', quadrant: 'techniques', ring: 'assess', description: 'Data residency requirements driving local cloud adoption.', isNew: true },
]

const QUADRANT_CONFIG: Record<string, { label: string; color: string }> = {
  languages: { label: 'Languages & Frameworks', color: '#3b82f6' },
  platforms: { label: 'Platforms', color: '#10b981' },
  tools: { label: 'Tools', color: '#f59e0b' },
  techniques: { label: 'Techniques', color: '#8b5cf6' },
}

const RING_CONFIG: Record<string, { label: string; description: string; bg: string; border: string }> = {
  adopt: { label: 'ADOPT', description: 'Proven, use with confidence', bg: 'bg-emerald/10', border: 'border-emerald/30' },
  trial: { label: 'TRIAL', description: 'Worth pursuing, low risk to try', bg: 'bg-blue/10', border: 'border-blue/30' },
  assess: { label: 'ASSESS', description: 'Worth exploring, understand impact', bg: 'bg-amber/10', border: 'border-amber/30' },
  hold: { label: 'HOLD', description: 'Proceed with caution', bg: 'bg-rose/10', border: 'border-rose/30' },
}

// ── SVG radar visualization (ThoughtWorks/AOE style) ─────────────────────────
// Quadrants are angular sectors (TL/TR/BL/BR), rings are radial bands, blips
// are placed within their (quadrant, ring) sector with deterministic jitter so
// the layout is stable across renders.

const RING_RADII = { adopt: 0.30, trial: 0.55, assess: 0.78, hold: 1.00 } as const
const RING_INNER  = { adopt: 0.00, trial: 0.30, assess: 0.55, hold: 0.78 } as const
// Quadrant angle ranges (radians, 0 = right, going counter-clockwise like maths)
const QUADRANT_ANGLES = {
  techniques: [Math.PI * 0.5, Math.PI],          // top-left
  tools:      [0, Math.PI * 0.5],                // top-right
  languages:  [Math.PI, Math.PI * 1.5],          // bottom-left
  platforms:  [Math.PI * 1.5, Math.PI * 2],      // bottom-right
} as const

// Stable hash → 0..1 for blip jitter within sector (so blips don't overlap on
// re-render and a tech keeps its position when others are added/removed).
function hash01(s: string, seed = 0): number {
  let h = seed
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0
  return ((h >>> 0) % 10_000) / 10_000
}

function RadarChart({ items, onBlipClick, highlight }: {
  items: (RadarItem & { id?: number })[]
  onBlipClick: (name: string) => void
  highlight: string | null
}) {
  const SIZE = 480
  const cx = SIZE / 2
  const cy = SIZE / 2
  const R = SIZE / 2 - 16

  // Place each blip
  const blips = items.map((item, idx) => {
    const [a0, a1] = QUADRANT_ANGLES[item.quadrant] || [0, Math.PI / 2]
    const rOuter = RING_RADII[item.ring] ?? 1
    const rInner = RING_INNER[item.ring] ?? 0
    // Padded so blips don't kiss the ring edges
    const rPad = (rOuter - rInner) * 0.15
    const r = rInner + rPad + (rOuter - rInner - 2 * rPad) * hash01(item.name, 1)
    const aPad = (a1 - a0) * 0.08
    const angle = a0 + aPad + (a1 - a0 - 2 * aPad) * hash01(item.name, 7)
    const x = cx + Math.cos(angle) * r * R
    const y = cy - Math.sin(angle) * r * R   // SVG y-axis is inverted
    return { ...item, x, y, num: idx + 1 }
  })

  return (
    <div className="card p-3 overflow-x-auto">
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="mx-auto block" style={{ maxWidth: SIZE, width: '100%' }}>
        {/* Quadrant background sectors — subtle tinting */}
        {(Object.entries(QUADRANT_ANGLES) as [keyof typeof QUADRANT_ANGLES, readonly [number, number]][]).map(([key, [a0, a1]]) => {
          const x0 = cx + Math.cos(a0) * R
          const y0 = cy - Math.sin(a0) * R
          const x1 = cx + Math.cos(a1) * R
          const y1 = cy - Math.sin(a1) * R
          const colour = QUADRANT_CONFIG[key].color
          return (
            <path key={`q-${key}`}
              d={`M ${cx} ${cy} L ${x0} ${y0} A ${R} ${R} 0 0 0 ${x1} ${y1} Z`}
              fill={colour} fillOpacity="0.04" stroke="none" />
          )
        })}

        {/* Concentric rings */}
        {(['adopt', 'trial', 'assess', 'hold'] as const).map(ring => (
          <circle key={`ring-${ring}`} cx={cx} cy={cy} r={RING_RADII[ring] * R}
            fill="none" stroke="#475569" strokeOpacity="0.4" strokeWidth="1" />
        ))}

        {/* Quadrant divider lines */}
        <line x1={0} y1={cy} x2={SIZE} y2={cy} stroke="#475569" strokeOpacity="0.4" strokeWidth="1" />
        <line x1={cx} y1={0} x2={cx} y2={SIZE} stroke="#475569" strokeOpacity="0.4" strokeWidth="1" />

        {/* Ring labels (along the top vertical) */}
        {(['adopt', 'trial', 'assess', 'hold'] as const).map(ring => (
          <text key={`rl-${ring}`} x={cx + 4} y={cy - RING_RADII[ring] * R + 4}
            fontSize="9" fill="#94a3b8" fontFamily="monospace" textAnchor="start">
            {ring.toUpperCase()}
          </text>
        ))}

        {/* Quadrant labels in corners */}
        <text x={16} y={20} fontSize="11" fill={QUADRANT_CONFIG.techniques.color} fontWeight="bold">Techniques</text>
        <text x={SIZE - 16} y={20} fontSize="11" fill={QUADRANT_CONFIG.tools.color} fontWeight="bold" textAnchor="end">Tools</text>
        <text x={16} y={SIZE - 8} fontSize="11" fill={QUADRANT_CONFIG.languages.color} fontWeight="bold">Languages</text>
        <text x={SIZE - 16} y={SIZE - 8} fontSize="11" fill={QUADRANT_CONFIG.platforms.color} fontWeight="bold" textAnchor="end">Platforms</text>

        {/* Blips */}
        {blips.map(blip => {
          const colour = QUADRANT_CONFIG[blip.quadrant].color
          const isHighlight = highlight === blip.name
          return (
            <g key={`${blip.name}-${blip.num}`}
              onClick={() => onBlipClick(blip.name)}
              style={{ cursor: 'pointer' }}>
              <title>{blip.name} — {blip.ring.toUpperCase()} · {QUADRANT_CONFIG[blip.quadrant].label}{blip.description ? `\n${blip.description}` : ''}</title>
              {blip.isNew && (
                <circle cx={blip.x} cy={blip.y} r={11} fill="none" stroke={colour} strokeOpacity="0.5" strokeWidth="1" strokeDasharray="2 2" />
              )}
              <circle cx={blip.x} cy={blip.y} r={isHighlight ? 9 : 7}
                fill={colour} fillOpacity={isHighlight ? 1 : 0.85}
                stroke={isHighlight ? '#fff' : 'rgba(0,0,0,0.25)'} strokeWidth={isHighlight ? 2 : 1} />
              <text x={blip.x} y={blip.y + 3} textAnchor="middle"
                fontSize="9" fill="#fff" fontWeight="bold" fontFamily="monospace"
                pointerEvents="none">
                {blip.num}
              </text>
            </g>
          )
        })}
      </svg>
      <p className="text-[9px] text-text-muted text-center mt-2">
        Click a blip to highlight in the list below · dashed ring = new in this revision · hover for description
      </p>
    </div>
  )
}

export default function TechRadarPage() {
  const [items, setItems] = useState<RadarItem[]>(RADAR_ITEMS)
  const [quadrantFilter, setQuadrantFilter] = useState<string>('all')
  const [ringFilter, setRingFilter] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [newItem, setNewItem] = useState({ name: '', quadrant: 'tools', ring: 'assess', description: '' })
  const [dbLoaded, setDbLoaded] = useState(false)
  const [highlight, setHighlight] = useState<string | null>(null)

  // Scroll highlighted blip into view in the list below
  useEffect(() => {
    if (!highlight) return
    const id = `radar-blip-${highlight.replace(/\s+/g, '-').toLowerCase()}`
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [highlight])

  // Load from DB on mount — auto-seed if empty
  useEffect(() => {
    api.get('/api/content/radar').then(({ data }) => {
      if (data.items?.length > 0) {
        setItems(data.items)
        setDbLoaded(true)
      } else {
        // Auto-seed on first load
        api.post('/api/content/radar/seed').then(() => {
          api.get('/api/content/radar').then(({ data: d2 }) => {
            if (d2.items?.length > 0) { setItems(d2.items); setDbLoaded(true) }
          })
        })
      }
    }).catch(() => {})
  }, [])

  const addItem = async () => {
    if (!newItem.name) return
    await api.post('/api/content/radar', { ...newItem, is_new: true })
    const { data } = await api.get('/api/content/radar')
    if (data.items?.length > 0) setItems(data.items)
    setNewItem({ name: '', quadrant: 'tools', ring: 'assess', description: '' })
    setShowAdd(false)
  }

  const deleteItem = async (id: number) => {
    await api.delete(`/api/content/radar/${id}`)
    setItems(items.filter(i => (i as any).id !== id))
  }

  const seedFromDefaults = async () => {
    await api.post('/api/content/radar/seed')
    const { data } = await api.get('/api/content/radar')
    if (data.items?.length > 0) { setItems(data.items); setDbLoaded(true) }
  }

  const filtered = items
    .filter(i => quadrantFilter === 'all' || i.quadrant === quadrantFilter)
    .filter(i => ringFilter === 'all' || i.ring === ringFilter)
    .filter(i => !searchTerm || i.name.toLowerCase().includes(searchTerm.toLowerCase()) || i.description.toLowerCase().includes(searchTerm.toLowerCase()))

  const rings = ['adopt', 'trial', 'assess', 'hold'] as const

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Radar size={20} className="text-purple" /> Technology Radar
          </h1>
          <p className="text-[10px] text-text-secondary">
            {items.length} technologies — {dbLoaded ? 'from database' : 'defaults (click Seed to save to DB)'}
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => setShowAdd(!showAdd)} className="btn-primary text-xs"><Plus size={12} /> Add</button>
          {!dbLoaded && <button type="button" onClick={seedFromDefaults} className="btn-secondary text-xs">Seed DB</button>}
        </div>
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="card p-4 border border-purple/20">
          <h2 className="text-xs font-semibold text-text-primary mb-2">Add Technology to Radar</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
            <input value={newItem.name} onChange={e => setNewItem({...newItem, name: e.target.value})} placeholder="Name" className="input py-1.5 text-xs" />
            <select title="Quadrant" value={newItem.quadrant} onChange={e => setNewItem({...newItem, quadrant: e.target.value})} className="input py-1.5 text-xs">
              {Object.entries(QUADRANT_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select title="Ring" value={newItem.ring} onChange={e => setNewItem({...newItem, ring: e.target.value})} className="input py-1.5 text-xs">
              {Object.keys(RING_CONFIG).map(k => <option key={k} value={k}>{k.toUpperCase()}</option>)}
            </select>
            <button type="button" onClick={addItem} className="btn-primary text-xs"><Plus size={12} /> Add</button>
          </div>
          <input value={newItem.description} onChange={e => setNewItem({...newItem, description: e.target.value})} placeholder="Description" className="input py-1.5 text-xs" />
        </div>
      )}

      {/* Visual radar */}
      <RadarChart items={items} onBlipClick={n => setHighlight(highlight === n ? null : n)} highlight={highlight} />

      {/* Ring legend */}
      <div className="grid grid-cols-4 gap-2">
        {rings.map(ring => {
          const cfg = RING_CONFIG[ring]
          const count = RADAR_ITEMS.filter(i => i.ring === ring).length
          return (
            <button key={ring} type="button" onClick={() => setRingFilter(ringFilter === ring ? 'all' : ring)}
              className={clsx('card p-2.5 text-center border transition-colors cursor-pointer', cfg.border, cfg.bg,
                ringFilter === ring && 'ring-2 ring-blue/40')}>
              <span className="text-xs font-bold text-text-primary">{cfg.label}</span>
              <p className="text-[9px] text-text-muted mt-0.5">{cfg.description}</p>
              <span className="text-sm font-bold text-text-primary mt-1 block">{count}</span>
            </button>
          )
        })}
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <button type="button" onClick={() => setQuadrantFilter('all')}
          className={clsx('badge text-[10px] cursor-pointer px-3 py-1.5', quadrantFilter === 'all' ? 'badge-blue' : 'bg-surface-card text-text-muted border border-border')}>
          All ({RADAR_ITEMS.length})
        </button>
        {Object.entries(QUADRANT_CONFIG).map(([key, cfg]) => (
          <button key={key} type="button" onClick={() => setQuadrantFilter(quadrantFilter === key ? 'all' : key)}
            className={clsx('badge text-[10px] cursor-pointer px-3 py-1.5',
              quadrantFilter === key ? 'badge-blue' : 'bg-surface-card text-text-muted border border-border')}>
            <span className="w-2 h-2 rounded-full inline-block mr-1" style={{ backgroundColor: cfg.color }} />
            {cfg.label} ({RADAR_ITEMS.filter(i => i.quadrant === key).length})
          </button>
        ))}
      </div>

      <div className="relative max-w-sm">
        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
        <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search technologies..." className="input pl-7 py-1.5 text-[11px]" />
      </div>

      {/* Radar items grouped by ring */}
      {rings.map(ring => {
        const ringItems = filtered.filter(i => i.ring === ring)
        if (ringItems.length === 0) return null
        const cfg = RING_CONFIG[ring]
        return (
          <div key={ring}>
            <h2 className="text-xs font-semibold text-text-primary mb-2 flex items-center gap-2">
              <span className={clsx('px-2 py-0.5 rounded text-[10px] font-bold', cfg.bg)}>{cfg.label}</span>
              <span className="text-text-muted font-normal">{cfg.description}</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {ringItems.map(item => {
                const qCfg = QUADRANT_CONFIG[item.quadrant]
                const isHi = highlight === item.name
                return (
                  <div key={item.name}
                    id={`radar-blip-${item.name.replace(/\s+/g, '-').toLowerCase()}`}
                    className={clsx('card p-3 border transition-all', cfg.border, cfg.bg, isHi && 'ring-2 ring-blue/60 scale-[1.02]')}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: qCfg.color }} />
                      <span className="text-xs font-semibold text-text-primary">{item.name}</span>
                      {item.isNew && <span className="badge-amber text-[8px]">NEW</span>}
                    </div>
                    <p className="text-[10px] text-text-secondary leading-relaxed">{item.description}</p>
                    <span className="text-[8px] text-text-muted mt-1 block">{qCfg.label}</span>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}
